import {
  normalizeSpecValues,
  type SpecAxis,
  type SpecValues,
} from "./sku-specs";

type SkuWithSpecs = { specs?: unknown };
type PurchaseState = { stock: number; endAt?: string; levelAllowed?: boolean };

export function initialShopSku<T extends PurchaseState & { serial: number }>(
  skus: T[],
  serial: number,
  now: number,
): T | undefined {
  return (
    skus.find((sku) => sku.serial === serial) ??
    skus.find((sku) => canPurchaseSku(sku, now)) ??
    skus.find((sku) => sku.stock > 0 && !hasEnded(sku, now)) ??
    skus.find((sku) => !hasEnded(sku, now)) ??
    skus[0]
  );
}

function hasEnded(sku: PurchaseState, now: number): boolean {
  return Boolean(sku.endAt && new Date(sku.endAt).getTime() <= now);
}

export function canPurchaseSku(sku: PurchaseState, now: number): boolean {
  return sku.stock > 0 && !hasEnded(sku, now) && sku.levelAllowed !== false;
}

export function visibleSpecValues(
  axes: SpecAxis[],
  selected: SpecValues,
  axisName: string,
  skus: SkuWithSpecs[],
): string[] {
  const index = axes.findIndex((axis) => axis.name === axisName);
  if (index < 0) return [];
  const prefix = axes.slice(0, index).filter((axis) => selected[axis.name]);
  return axes[index]!.values.filter((value) =>
    skus.some((sku) => {
      const specs = normalizeSpecValues(sku.specs);
      return (
        specs[axisName] === value &&
        prefix.every((axis) => specs[axis.name] === selected[axis.name])
      );
    }),
  );
}

export function selectShopSpec<T extends SkuWithSpecs>(
  axes: SpecAxis[],
  selected: SpecValues,
  axisName: string,
  value: string,
  skus: T[],
  preferred: (sku: T) => boolean,
): T | undefined {
  const index = axes.findIndex((axis) => axis.name === axisName);
  if (index < 0) return undefined;
  const prefix = axes.slice(0, index);
  const candidates = skus.filter((sku) => {
    const specs = normalizeSpecValues(sku.specs);
    return (
      specs[axisName] === value &&
      prefix.every(
        (axis) =>
          !selected[axis.name] || specs[axis.name] === selected[axis.name],
      )
    );
  });
  const preferredRows = candidates.filter(preferred);
  const pool = preferredRows.length ? preferredRows : candidates;
  const downstream = axes.slice(index + 1);
  const score = (sku: T) => {
    const specs = normalizeSpecValues(sku.specs);
    return downstream.filter((axis) => specs[axis.name] === selected[axis.name])
      .length;
  };
  return pool.reduce<T | undefined>(
    (best, sku) => (!best || score(sku) > score(best) ? sku : best),
    undefined,
  );
}
